"use client";

import { useTranslations } from "next-intl";
import { use, useCallback, useRef, useState, useTransition } from "react";
import { useDebounceCallback } from "usehooks-ts";

import { cn } from "@trip/utils";

import { MinusIcon } from "~/components/icons/minus-icon";
import { PlusIcon } from "~/components/icons/plus-icon";
import { LoadingSpinner } from "~/components/loading-spinner";
import { Button } from "~/components/ui/button";
import { useTrack } from "~/lib/use-track";
import { useRouter } from "~/navigation";
import { trpcReact } from "~/trpc/client";

import { TripContext } from "../_lib/trip-context";

interface AddRemoveNightsProps {
  destinationDatoId: string;
  currentNights: number;
}

export function AddRemoveNights({
  destinationDatoId,
  currentNights,
}: AddRemoveNightsProps) {
  const t = useTranslations("Common");
  const track = useTrack();
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const tripContext = use(TripContext);
  if (!tripContext) {
    throw new Error("TripContext is not available");
  }
  const { datoTrip, lineItem } = tripContext;

  const datoTripActionMutation = trpcReact.cart.datoTripAction.useMutation();
  const cartTripActionMutation = trpcReact.cart.cartTripAction.useMutation();

  const isDatoTrip = lineItem === null;
  const isCartTrip = lineItem !== null;

  // Track last known server value to detect changes
  const lastCurrentNightsRef = useRef(currentNights);
  const [optimisticNights, setOptimisticNights] = useState(currentNights);

  // Check if server state changed and sync if needed
  if (lastCurrentNightsRef.current !== currentNights) {
    lastCurrentNightsRef.current = currentNights;
    setOptimisticNights(currentNights);
  }

  // Accumulate deltas for batching
  const pendingDeltaRef = useRef<number>(0);

  const executeNightsChange = useCallback(
    async (totalDelta: number) => {
      if (totalDelta === 0) return;

      const isAdding = totalDelta > 0;
      const actionType = isAdding
        ? "ADD_DESTINATION_NIGHT"
        : "REMOVE_DESTINATION_NIGHT";
      const iterations = Math.abs(totalDelta);

      if (isDatoTrip) {
        const destinationIndex = datoTrip.tripDestinations.findIndex(
          (dest) => dest.id === destinationDatoId,
        );
        if (destinationIndex >= 0) {
          // Execute multiple operations for the accumulated delta
          for (let i = 0; i < iterations; i++) {
            await datoTripActionMutation.mutateAsync({
              type: actionType,
              datoTripId: datoTrip.id,
              destinationIndex,
            });
          }
        }
      } else if (isCartTrip) {
        const cartDestination = lineItem.destinations.find(
          (dest) => dest.datoTripDestinationId === destinationDatoId,
        );
        if (cartDestination) {
          // Execute multiple operations for the accumulated delta
          for (let i = 0; i < iterations; i++) {
            await cartTripActionMutation.mutateAsync({
              type: actionType,
              tripLineItemId: lineItem.id,
              destinationId: cartDestination.id,
            });
          }
        }
      }

      // Track the final accumulated change
      track({
        name: "Change Destination Nights",
        datoTripId: datoTrip.id,
        destinationId: destinationDatoId,
        nights: optimisticNights, // Use optimistic value for tracking
        added: totalDelta,
      });

      router.refresh();
    },
    [
      isDatoTrip,
      isCartTrip,
      datoTrip,
      destinationDatoId,
      lineItem,
      optimisticNights,
      datoTripActionMutation,
      cartTripActionMutation,
      track,
      router,
    ],
  );

  // Debounced function that executes the accumulated changes
  const debouncedExecute = useDebounceCallback(() => {
    const deltaToExecute = pendingDeltaRef.current;
    if (deltaToExecute !== 0) {
      // Reset the pending delta BEFORE execution to prevent double counting
      pendingDeltaRef.current = 0;
      startTransition(async () => {
        await executeNightsChange(deltaToExecute);
      });
    }
  }, 600);

  const handleChange = useCallback(
    (delta: number) => {
      // Prevent adding negative nights
      if (optimisticNights + delta < 1) {
        return;
      }

      // Update optimistic state immediately
      setOptimisticNights((prev) => prev + delta);

      // Accumulate the delta
      pendingDeltaRef.current += delta;

      // Trigger debounced execution
      debouncedExecute();
    },
    [optimisticNights, debouncedExecute],
  );

  const handleAddNight = useCallback(() => {
    handleChange(1);
  }, [handleChange]);

  const handleRemoveNight = useCallback(() => {
    if (optimisticNights <= 1) return;
    handleChange(-1);
  }, [optimisticNights, handleChange]);

  const isRemoveDisabled = optimisticNights <= 1;

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="ghost"
        size="circle"
        onClick={handleRemoveNight}
        disabled={isRemoveDisabled || isPending}
        className={cn("size-8", isPending && "opacity-50")}
        aria-label="Remove one night"
      >
        <MinusIcon className="size-4" />
      </Button>

      <div className="relative min-w-[2ch]">
        <span
          className={cn(
            "block text-center text-sm font-medium",
            isPending && "invisible",
          )}
        >
          {t("accommodationNights", { nights: optimisticNights })}
        </span>
        {isPending ? (
          <LoadingSpinner className="absolute inset-0 m-auto size-4" />
        ) : null}
      </div>

      <Button
        variant="ghost"
        size="circle"
        onClick={handleAddNight}
        disabled={isPending}
        className={cn("size-8", isPending && "opacity-50")}
        aria-label="Add one night"
      >
        <PlusIcon className="size-4" />
      </Button>
    </div>
  );
}
